const SCHEMA_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]
const DAY = 86400000
const HORIZON_DAYS = 400
const ISO_DAYS = [1, 2, 3, 4, 5, 6, 7]

const slotsOf = (week, day) =>
  (week?.[day] || []).reduce((slots, slot) => {
    const last = slots[slots.length - 1]

    return last && last.end === slot.start
      ? [...slots.slice(0, -1), { start: last.start, end: slot.end }]
      : [...slots, slot]
  }, [])

const sameSlots = (a, b) =>
  a.length === b.length &&
  a.every(
    (slot, index) => slot.start === b[index].start && slot.end === b[index].end
  )

const wallClock = (instant, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant)
  const part = type => parts.find(p => p.type === type).value
  return {
    date: `${part('year')}-${part('month')}-${part('day')}`,
    time: `${part('hour')}:${part('minute')}`,
  }
}

const dateAt = date => Date.parse(`${date}T00:00:00Z`)

const isoDayOf = date => ((new Date(dateAt(date)).getUTCDay() + 6) % 7) + 1

const addDays = (date, days) =>
  new Date(dateAt(date) + days * DAY).toISOString().slice(0, 10)

export function storeState(
  { week, closures = [], timezone = 'Europe/Paris' },
  instant = new Date()
) {
  const { date, time } = wallClock(instant, timezone)
  const closed = new Set(closures)
  const closedToday = closed.has(date) ? 'holiday' : null
  const current = closedToday
    ? null
    : slotsOf(week, isoDayOf(date)).find(
        slot => slot.start <= time && time < slot.end
      )

  if (current) {
    return { open: true, closesAt: current.end }
  }

  for (let offset = 0; offset <= HORIZON_DAYS; offset++) {
    const day = addDays(date, offset)
    const slot = closed.has(day)
      ? null
      : slotsOf(week, isoDayOf(day)).find(
          candidate => offset > 0 || candidate.start > time
        )

    if (slot) {
      return { open: false, opensAt: slot.start, opensOn: day, closedToday }
    }
  }

  return { open: false, opensAt: null, opensOn: null, closedToday }
}

export function openingDayGroups(week, { withClosedDays = false } = {}) {
  return ISO_DAYS.reduce((groups, day) => {
    const slots = slotsOf(week, day)
    const last = groups[groups.length - 1]

    if (!slots.length && !withClosedDays) {
      return groups
    }

    if (
      last &&
      last.days[last.days.length - 1] === day - 1 &&
      sameSlots(last.slots, slots)
    ) {
      last.days.push(day)
    } else {
      groups.push({ days: [day], slots })
    }

    return groups
  }, [])
}

export function openingHoursSpecification(week) {
  return ISO_DAYS.reduce((specifications, day) => {
    for (const slot of slotsOf(week, day)) {
      const specification = specifications.find(
        candidate =>
          candidate.opens === slot.start && candidate.closes === slot.end
      )

      if (specification) {
        specification.dayOfWeek.push(SCHEMA_DAYS[day - 1])
      } else {
        specifications.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: [SCHEMA_DAYS[day - 1]],
          opens: slot.start,
          closes: slot.end,
        })
      }
    }

    return specifications
  }, [])
}

const timeOfDay = time => {
  const [hours, minutes] = time.split(':').map(Number)
  return new Date(Date.UTC(2024, 0, 1, hours, minutes))
}

export function weekdayName(day, locale, weekday = 'long') {
  return new Intl.DateTimeFormat(locale, { weekday, timeZone: 'UTC' }).format(
    new Date(Date.UTC(2024, 0, day))
  )
}

export function openDayRange(week) {
  const days = ISO_DAYS.filter(day => slotsOf(week, day).length)
  const first = days[0]
  const last = days[days.length - 1]

  return days.length > 1 && last - first === days.length - 1
    ? { first, last }
    : null
}

export function formatOpeningDayGroups(groups, locale, weekday = 'long') {
  const timeFormat = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  })
  const dayName = day => weekdayName(day, locale, weekday)
  const capitalized = text =>
    text.charAt(0).toLocaleUpperCase(locale) + text.slice(1)
  const range = slot =>
    typeof timeFormat.formatRange === 'function'
      ? timeFormat.formatRange(timeOfDay(slot.start), timeOfDay(slot.end))
      : `${timeFormat.format(timeOfDay(slot.start))} – ${timeFormat.format(
          timeOfDay(slot.end)
        )}`

  return groups.map(({ days, slots }) => ({
    days: capitalized(
      days.length === 1
        ? dayName(days[0])
        : `${dayName(days[0])} – ${dayName(days[days.length - 1])}`
    ),
    slots: slots.map(range),
  }))
}

export function openingHoursLine(week, locale, weekday = 'short') {
  return formatOpeningDayGroups(openingDayGroups(week), locale, weekday)
    .map(group => `${group.days} ${group.slots.join(', ')}`)
    .join(' · ')
}
